import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getBlogArticles, getBlogCategories, getBlogTags } from '../../api';
import { FiClock, FiEye, FiTag, FiSearch, FiX } from 'react-icons/fi';
import './Blog.css';

const Blog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);

  const search = searchParams.get('search') || '';
  const category = searchParams.get('category') || '';
  const tag = searchParams.get('tag') || '';
  const PAGE_SIZE = 9;

  const fetchArticles = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE, ordering: '-created_at', is_published: true };
    if (search) params.search = search;
    if (category) params.category = category;
    if (tag) params.tags = tag;
    getBlogArticles(params)
      .then(({ data }) => { setArticles(data.results || []); setCount(data.count || 0); })
      .finally(() => setLoading(false));
  }, [page, search, category, tag]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);
  useEffect(() => { getBlogCategories().then(({ data }) => setCategories(data.results || [])); }, []);
  useEffect(() => { getBlogTags().then(({ data }) => setTags(data.results || [])); }, []);
  useEffect(() => { setPage(1); }, [search, category, tag]);

  const setParam = (key, val) => {
    const p = new URLSearchParams(searchParams);
    if (val) p.set(key, val); else p.delete(key);
    setSearchParams(p);
  };

  const totalPages = Math.ceil(count / PAGE_SIZE);

  return (
    <div className="blog-page">
      <div className="blog-hero">
        <div className="container">
          <div className="blog-hero-eyebrow">DJI Nepal Blog</div>
          <h1 className="blog-hero-title">News, Tips & Guides</h1>
          <p className="blog-hero-sub">Stay updated with the latest DJI news, drone tips, and photography guides.</p>
          <div className="blog-search">
            <FiSearch className="blog-search-icon" />
            <input
              type="text"
              placeholder="Search articles..."
              value={search}
              onChange={(e) => setParam('search', e.target.value)}
            />
            {search && <button onClick={() => setParam('search', '')}><FiX /></button>}
          </div>
        </div>
      </div>

      <div className="container blog-body">
        {/* Filters */}
        <div className="blog-filters">
          {/* Categories */}
          {categories.length > 0 && (
            <div className="filter-group">
              <span className="filter-label">Category:</span>
              <button className={`filter-chip ${!category ? 'active' : ''}`} onClick={() => setParam('category', '')}>All</button>
              {categories.map((c) => (
                <button key={c.id} className={`filter-chip ${category === c.slug ? 'active' : ''}`} onClick={() => setParam('category', c.slug)}>
                  {c.name}
                </button>
              ))}
            </div>
          )}

          {/* Tags */}
          {tags.length > 0 && (
            <div className="filter-group">
              <span className="filter-label"><FiTag /> Tags:</span>
              {tags.slice(0, 10).map((t) => (
                <button key={t.id} className={`filter-chip tag-chip ${tag === t.slug ? 'active' : ''}`} onClick={() => setParam('tag', tag === t.slug ? '' : t.slug)}>
                  #{t.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Results count */}
        <div className="blog-count">{count} article{count !== 1 ? 's' : ''} found</div>

        {/* Articles Grid */}
        {loading ? (
          <div className="blog-grid">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="skeleton" style={{ height: 320, borderRadius: 8 }} />
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="blog-empty">
            <div style={{ fontSize: 48 }}>📝</div>
            <h3>No articles found</h3>
            <p className="text-muted">Try adjusting your search or filters</p>
            <button className="btn btn-primary" onClick={() => setSearchParams({})}>Clear Filters</button>
          </div>
        ) : (
          <div className="blog-grid">
            {articles.map((article) => (
              <Link key={article.id} to={`/blog/${article.slug}`} className="blog-card">
                <div className="blog-card-img">
                  {article.images?.length > 0 ? (
                    <img src={article.images[0].image} alt={article.images[0].alt || article.title} loading="lazy" />
                  ) : article.featured_image ? (
                    <img src={article.featured_image} alt={article.title} loading="lazy" />
                  ) : (
                    <div className="blog-card-img-placeholder">📷</div>
                  )}
                </div>
                <div className="blog-card-body">
                  {article.category && <span className="blog-card-cat">{article.category?.name || article.category}</span>}
                  <h2 className="blog-card-title">{article.title}</h2>
                  {article.meta_description && <p className="blog-card-excerpt">{article.meta_description}</p>}
                  <div className="blog-card-meta">
                    {article.read_time && <span><FiClock /> {article.read_time} min read</span>}
                    {article.views !== undefined && <span><FiEye /> {article.views} views</span>}
                    <span>{new Date(article.created_at).toLocaleDateString('en-NP', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                  </div>
                  {article.tags?.length > 0 && (
                    <div className="blog-card-tags">
                      {article.tags.slice(0, 3).map((t) => (
                        <span key={t.id || t} className="blog-tag">#{t.name || t}</span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button className="btn btn-outline btn-sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
            <span className="page-info">Page {page} of {totalPages}</span>
            <button className="btn btn-outline btn-sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next →</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
